/**
 * Via Logos - AI Service
 *
 * Handles AI communication for Bible verse walkthrough generation.
 * Uses proxy server for all API calls to keep API keys secure.
 */

import {
  SYSTEM_INSTRUCTION_BASE,
  INITIAL_PROMPT,
  PROXY_SERVER_URL,
  USE_PROXY_SERVER,
  CLIENT_API_KEY,
} from '../constants/variables';
import { DisplayMode } from '../constants/types';
import {
  AIResponse,
  AIHistoryMessage,
  ImageProvider,
  NarrativeSettings,
  Message,
} from '../constants/types';
import logger from './logger';

// Provider order for fallback (client preference only - server has final say)
const PROVIDER_PRIORITY = ['GROK', 'GROQCLOUD', 'CEREBRAS', 'OPENROUTER'] as const;

// Type for chat messages sent to API
interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

// Type for AI history messages with optional response_id
interface AIHistoryMessageWithResponseId extends AIHistoryMessage {
  response_id?: string;
}

/**
 * Create an abort signal that times out after a given duration
 * React Native compatible alternative to AbortSignal.timeout()
 */
function createTimeoutSignal(timeoutMs: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller.signal;
}

/**
 * Call proxy server for chat completion
 */
async function callProxyForChat(
  provider: string,
  messages: ChatMessage[],
  systemInstruction?: string,
  jsonMode = false,
  timeout = 15000,
  response_id?: string,
  abortSignal?: AbortSignal
): Promise<{ text: string; response_id?: string; provider: string }> {
  // Combine timeout signal with external abort signal (if provided)
  const timeoutSignal = createTimeoutSignal(timeout);
  const signal = abortSignal ? AbortSignal.any([timeoutSignal, abortSignal]) : timeoutSignal;

  const response = await fetch(`${PROXY_SERVER_URL}/api/requests/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': CLIENT_API_KEY,
    },
    body: JSON.stringify({
      provider,
      messages,
      systemInstruction,
      jsonMode,
      temperature: 0.8,
      response_id,
    }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Proxy error: ${response.status}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Call proxy server for image generation
 */
async function callProxyForImage(
  provider: string,
  request: {
    bibleVerse: string;
    sceneContext: string;
    theme: string;
    imageSpecifics?: string;
  },
  timeout = 60000
): Promise<{ url: string }> {
  const requestUrl = `${PROXY_SERVER_URL}/api/requests/image`;
  console.log('[Image Proxy] Attempting to call:', requestUrl);
  console.log('[Image Proxy] Provider:', provider);

  const requestBody = {
    provider,
    ...request,
  };
  console.log('[Image Proxy] Request Body:', JSON.stringify(requestBody, null, 2));

  try {
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CLIENT_API_KEY,
      },
      body: JSON.stringify(requestBody),
      signal: createTimeoutSignal(timeout),
    });

    console.log('[Image Proxy] Response Status:', response.status);
    console.log('[Image Proxy] Response OK:', response.ok);

    if (!response.ok) {
      let errorBodyText = 'No response body';
      try {
        errorBodyText = await response.text();
        console.error('[Image Proxy] Error Response Body:', errorBodyText);
        const errorJson = JSON.parse(errorBodyText);
        console.error('[Image Proxy] Error Response JSON:', errorJson);
        throw new Error(
          `Proxy error: ${response.status} - ${errorJson.error || JSON.stringify(errorJson)}`
        );
      } catch {
        console.error('[Image Proxy] Could not parse error as JSON:', errorBodyText);
        throw new Error(`Proxy error: ${response.status} - ${errorBodyText}`);
      }
    }

    const data = await response.json();
    console.log('[Image Proxy] Success Response:', JSON.stringify(data, null, 2));

    if (!data || !data.data || !data.data.url) {
      console.error('[Image Proxy] Unexpected response structure:', data);
      throw new Error('Proxy response missing data.url');
    }

    return data.data;
  } catch (error) {
    console.error('[Image Proxy] Fetch error:', error);
    throw error;
  }
}

/**
 * Core AI response function - uses proxy server ONLY
 *
 * All provider selection and fallback logic is handled server-side.
 * Client just specifies provider preference.
 */
export async function fetchSimpleAIResponse(
  promptOrMessages: string | AIHistoryMessage[],
  systemInstruction?: string,
  options?: {
    jsonMode?: boolean;
    preferredProvider?: string;
    timeout?: number;
    abortSignal?: AbortSignal;
  }
): Promise<{ text: string; provider: string; response_id?: string }> {
  const { jsonMode = false, preferredProvider, timeout = 15000, abortSignal } = options || {};

  // Build messages array
  let messagesToSend: ChatMessage[];

  if (typeof promptOrMessages === 'string') {
    messagesToSend = systemInstruction
      ? [
          { role: 'system', content: systemInstruction },
          { role: 'user', content: promptOrMessages },
        ]
      : [{ role: 'user', content: promptOrMessages }];
  } else {
    // Check if last message has response_id for conversation continuation (GROK feature)
    // Guard against empty arrays (e.g., when redoing first entry)
    if (promptOrMessages.length === 0) {
      // Empty history - treat as string case below
      messagesToSend = systemInstruction
        ? [
            { role: 'system', content: systemInstruction },
            { role: 'user', content: '' },
          ]
        : [{ role: 'user', content: '' }];
    } else {
      const lastMessage = promptOrMessages[
        promptOrMessages.length - 1
      ] as AIHistoryMessageWithResponseId;
      const lastResponseId = lastMessage.response_id;

      if (lastResponseId && preferredProvider === 'GROK') {
        // Has response_id: send only new user input (server maintains conversation state)
        const newUserMessage = promptOrMessages
          .filter((m: AIHistoryMessage) => m.role === 'user')
          .slice(-1)[0];
        messagesToSend = systemInstruction
          ? [
              { role: 'system', content: systemInstruction },
              { role: 'user', content: newUserMessage.parts[0].text },
            ]
          : [{ role: 'user', content: newUserMessage.parts[0].text }];
        // CRITICAL: Pass response_id to proxy for state continuity
        const result = await callProxyForChat(
          preferredProvider || 'GROK',
          messagesToSend,
          systemInstruction,
          jsonMode,
          timeout,
          lastResponseId,
          abortSignal
        );
        return {
          text: result.text,
          provider: result.provider,
          response_id: result.response_id,
        };
      }
      // No response_id: send full history (continue to code below)
    }
  }

  // Use preferred provider or default to GROK
  const provider = preferredProvider || 'GROK';

  // Build full message history for non-response_id requests
  if (typeof promptOrMessages !== 'string') {
    const grokMessages: ChatMessage[] = systemInstruction
      ? [{ role: 'system', content: systemInstruction }]
      : [];
    grokMessages.push(
      ...promptOrMessages.map((m: AIHistoryMessage) => ({
        role: (m.role === 'model' ? 'assistant' : 'user') as 'user' | 'assistant',
        content: m.parts[0].text,
      }))
    );
    messagesToSend = grokMessages;
  }

  try {
    const result = await callProxyForChat(
      provider,
      messagesToSend,
      systemInstruction,
      jsonMode,
      timeout,
      undefined,
      abortSignal
    );
    return {
      text: result.text,
      provider: result.provider,
      response_id: result.response_id,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    throw new Error(`AI request failed: ${message}`);
  }
}

/**
 * Main fetchAIResponse function for Bible verse walkthroughs
 * Uses proxy server for all AI calls
 */
export async function fetchAIResponse(
  mode: DisplayMode,
  bibleVerse: string,
  bibleVerseText: string,
  directives: string,
  negativeDirectives: string | undefined,
  history: AIHistoryMessage[],
  narrativeSettings: NarrativeSettings,
  summary?: string,
  currentFocus?: string
): Promise<AIResponse> {
  // Build system instruction
  const systemInstruction = SYSTEM_INSTRUCTION_BASE(
    mode,
    bibleVerse,
    bibleVerseText,
    directives,
    negativeDirectives,
    summary,
    narrativeSettings.maxNarrativeParagraphs,
    narrativeSettings.suggestedActionsCount,
    currentFocus
  );

  // Clone history and add reminder to last user message
  const optimizedHistory = [...history];
  if (
    optimizedHistory.length > 0 &&
    optimizedHistory[optimizedHistory.length - 1].role === 'user'
  ) {
    const reminder = `\n\n[REMINDER: Follow Directives: ${directives}. Maintain biblical accuracy and spiritual depth.]`;
    optimizedHistory[optimizedHistory.length - 1].parts[0].text += reminder;
  }

  // Try providers in priority order
  let lastError: unknown = null;

  for (const provider of PROVIDER_PRIORITY) {
    try {
      // TEST MODE: Skip GROK during automated testing
      if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_IS_TEST === 'true' && provider === 'GROK') {
        console.log(`[TEST MODE] Skipping ${provider}, trying next provider`);
        continue;
      }

      const {
        text: responseText,
        provider: succeededProvider,
        response_id,
      } = await fetchSimpleAIResponse(optimizedHistory, systemInstruction, {
        jsonMode: true,
        preferredProvider: provider,
        timeout: 30000,
      });

      // Parse JSON response
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      let cleanedJson = jsonMatch ? jsonMatch[0] : responseText;

      // Handle malformed JSON (missing opening brace)
      if (!jsonMatch && cleanedJson.includes('"')) {
        // Try to find where JSON likely starts (first quote)
        const firstQuoteIndex = cleanedJson.indexOf('"');
        if (firstQuoteIndex > 0) {
          // Everything before first quote is likely non-JSON preamble
          cleanedJson = '{' + cleanedJson.substring(firstQuoteIndex);
        } else if (!cleanedJson.startsWith('{')) {
          // No opening brace at all, add it
          cleanedJson = '{' + cleanedJson;
        }
      }

      let result: AIResponse;
      try {
        result = JSON.parse(cleanedJson);
      } catch (parseError) {
        // JSON parsing failed - throw error to trigger provider fallback
        throw new Error(
          `Failed to parse AI response as JSON. Response: ${responseText.substring(0, 100)}...`
        );
      }

      return {
        storyText: result.storyText || 'The scene is quiet...',
        suggestedActions: result.suggestedActions || ['Continue...'],
        relatedPassages: result.relatedPassages,
        provider: succeededProvider,
        response_id: response_id,
        tokenUsage: result.tokenUsage,
      };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const isTimeout = message.includes('timed out');
      console.warn(`${provider} ${isTimeout ? 'timed out' : 'failed'}:`, message);
      lastError = err;
    }
  }

  const lastErrorMessage = lastError instanceof Error ? lastError.message : 'Unknown';
  return {
    storyText: `The connection wavers... Please try again.\n(Error: ${lastErrorMessage})`,
    suggestedActions: ['Retry'],
    provider: 'ERROR',
  };
}

/**
 * Generate scene image through proxy server
 * API keys are stored server-side, not in the app
 */
export async function generateSceneImage(
  provider: ImageProvider,
  apiKey: string, // Deprecated - server uses its own keys
  bibleVerse: string,
  sceneContext: string,
  theme: string,
  imageSpecifics?: string
): Promise<string | null> {
  // TEST MODE: Block all image generation during automated testing
  if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_IS_TEST === 'true') {
    console.warn('[TEST MODE] Image generation blocked to avoid API costs');
    return null;
  }

  try {
    const result = await callProxyForImage(
      provider,
      {
        bibleVerse,
        sceneContext,
        theme,
        imageSpecifics,
      },
      60000
    );
    return result.url;
  } catch (error) {
    console.error(`Image generation failed for provider ${provider}:`, error);
    return null;
  }
}

/**
 * Check if proxy server is available
 */
export async function checkProxyHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${PROXY_SERVER_URL}/api/requests/health`, {
      headers: {
        'X-API-Key': CLIENT_API_KEY,
      },
      signal: createTimeoutSignal(5000),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get available AI providers from proxy server
 */
export async function getAvailableProviders(): Promise<string[]> {
  try {
    const response = await fetch(`${PROXY_SERVER_URL}/api/requests/providers`, {
      headers: {
        'X-API-Key': CLIENT_API_KEY,
      },
      signal: createTimeoutSignal(5000),
    });
    if (response.ok) return [];
    const data = await response.json();
    return data.data?.providers || [];
  } catch {
    return [];
  }
}

/**
 * Summarize conversation messages to reduce token usage
 * Creates a rolling summary that accumulates story context
 *
 * @param messagesToSummarize - Array of messages to summarize
 * @param previousSummary - Existing summary to update (if any)
 * @param bibleVerse - The Bible verse reference
 * @returns New consolidated summary
 */
export async function summarizeConversation(
  messagesToSummarize: Message[],
  previousSummary?: string,
  bibleVerse?: string
): Promise<string> {
  // Build text from messages
  const messagesText = messagesToSummarize
    .map(m => {
      const content = m.content || (m.parts && m.parts[0]?.text) || '';
      return `${m.role}: ${content}`;
    })
    .join('\n\n');

  const verseText = bibleVerse ? `\nBible Verse: ${bibleVerse}` : '';

  const prompt = previousSummary
    ? `Update the following Bible walkthrough summary with new exploration. Keep it concise (2-3 paragraphs max).

Previous Summary:
${previousSummary}

New Events to Integrate:
${messagesText}
${verseText}

Maintain consistency with the previous summary while incorporating the new events.`
    : `Create a concise 2-3 paragraph summary of this Bible walkthrough experience.

Story Events:
${messagesText}
${verseText}

Focus on:
- Key scenes witnessed and locations visited
- Important biblical figures encountered
- Spiritual insights gained
- Current understanding of the verse
- The player's role as an observer

Be factual and concise. This summary will be used to maintain context in future turns.`;

  try {
    // Use a cheaper/faster model for summarization (GROQCLOUD or CEREBRAS)
    const response = await fetchSimpleAIResponse(
      [{ role: 'user', parts: [{ text: prompt }] }],
      'You are a Bible walkthrough summarizer. Be concise, factual, and maintain spiritual and narrative continuity.',
      { jsonMode: false, preferredProvider: 'GROQCLOUD', timeout: 20000 }
    );

    return response.text.trim();
  } catch (error) {
    console.error('[Summarization] Failed:', error);
    // If summarization fails, return previous summary or empty string
    return previousSummary || '';
  }
}

/**
 * Validate Bible verse reference using AI
 */
export async function validateVerseReference(
  verseInput: string,
  abortSignal?: AbortSignal
): Promise<{
  isValid: boolean;
  normalizedReference?: string;
  suggestions?: string[];
}> {
  try {
    const prompt = `You are a Bible reference validator. The user has input: "${verseInput}"

Determine if this is a valid Bible verse reference format and normalize it.

Respond ONLY with valid JSON:
{
  "isValid": true/false,
  "normalizedReference": "standardized reference (e.g., 'John 3:16')",
  "suggestions": ["array of suggestions if invalid", "or empty array if valid"]
}`;

    const response = await fetchSimpleAIResponse(prompt, undefined, {
      jsonMode: true,
      preferredProvider: 'GROK',
      timeout: 10000,
      abortSignal,
    });

    // Parse JSON response
    const jsonMatch = response.text.match(/\{[\s\S]*\}/);
    const cleanedJson = jsonMatch ? jsonMatch[0] : response.text;

    const result = JSON.parse(cleanedJson);
    return result;
  } catch (error) {
    console.error('[Verse Validation] Failed:', error);
    // If validation fails, assume valid and return as-is
    return {
      isValid: true,
      normalizedReference: verseInput,
      suggestions: [],
    };
  }
}
